// Vektor-Tile-POI-Layer (drinking_water + fast_food)
// ===================================================
//
// Phase 1 der POI-Migration (siehe docs/poi-vector-tile-migration.md):
// Rendert drinking_water- und fast_food-POIs DIREKT aus dem `poi`-Source-Layer
// der OpenMapTiles-Basemap (Vektor-Tiles), statt aus dem alten Server-Blob-
// Download. Möglich seit die Korridor-Tiles auf z14 gebaut werden — erst ab
// z14 schreibt OpenMapTiles Amenity-POIs (drinking_water/fast_food) in den
// `poi`-Layer (z≤13 = nur Bahnhöfe).
//
// Bewusst additiv/parallel zum Blob-System (poiSymbol.ts / overpassPoiLayer.ts):
//   - Die Icons werden 1:1 wiederverwendet (💧 aus poiSymbol, 🥙 aus
//     overpassPoiLayer) — kein neues Sprite-Asset nötig.
//   - Ein Feature-Flag (useVectorPoiEnabled) schaltet zwischen Vektor (default)
//     und Blob (Fallback). Ist der Flag aus, werden diese Layer entfernt und
//     das Blob-System übernimmt wie bisher.
//
// Datenquelle: source 'basemap' (schon im Style vorhanden), source-layer 'poi'.
// Feature-Properties im poi-Layer: { class, subclass, name, rank, ... } — KEIN
// lat/lon (kommt aus der Geometrie) und keine Öffnungszeiten.

import type maplibregl from 'maplibre-gl';
import { ensurePoiIcon, POI_DW_ICON } from './poiSymbol';
import { ensureOverpassPoiIcons, overpassIconKey } from './overpassPoiLayer';

// Die Basemap-Vektor-Source (in style.ts als 'basemap' angelegt) + ihr
// OpenMapTiles-`poi`-Source-Layer.
const BASEMAP_SOURCE = 'basemap';
const POI_SOURCE_LAYER = 'poi';

export const VECTOR_POI_DW_LAYER = 'adt-vec-poi-drinking-water';
export const VECTOR_POI_FF_LAYER = 'adt-vec-poi-fast-food';

// Ab hier rendern wir die Vektor-POIs. Der `poi`-Layer hat drinking_water/
// fast_food erst ab z14 (rank-basiert); darunter ist ohnehin nichts da. 12 als
// defensiver Floor, damit MapLibre gar nicht erst bei Übersichts-Zooms sucht.
const VECTOR_POI_MIN_ZOOM = 12;

/** Registriert die wiederverwendeten Icons (idempotent). */
function ensureIcons(map: maplibregl.Map): void {
  ensurePoiIcon(map); // 💧 drinking_water
  ensureOverpassPoiIcons(map); // u.a. 🥙 fast_food
}

/**
 * Legt die zwei Symbol-Layer auf der Basemap-Vektor-Source an (idempotent).
 * Muss NACH ensureIcons laufen (tut es hier intern), sonst „image not found".
 */
export function ensureVectorPoiLayers(map: maplibregl.Map): void {
  // Ohne die Basemap-Source (z.B. Raster-Fallback-Style) gibt es keinen
  // poi-Layer → nichts zu tun.
  if (!map.getSource(BASEMAP_SOURCE)) return;
  ensureIcons(map);

  if (!map.getLayer(VECTOR_POI_DW_LAYER)) {
    map.addLayer({
      id: VECTOR_POI_DW_LAYER,
      type: 'symbol',
      source: BASEMAP_SOURCE,
      'source-layer': POI_SOURCE_LAYER,
      minzoom: VECTOR_POI_MIN_ZOOM,
      // subclass = roher OSM-Wert → robustester Filter.
      filter: ['==', ['get', 'subclass'], 'drinking_water'],
      layout: {
        'icon-image': POI_DW_ICON,
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });
  }
  if (!map.getLayer(VECTOR_POI_FF_LAYER)) {
    map.addLayer({
      id: VECTOR_POI_FF_LAYER,
      type: 'symbol',
      source: BASEMAP_SOURCE,
      'source-layer': POI_SOURCE_LAYER,
      minzoom: VECTOR_POI_MIN_ZOOM,
      // fast_food landet in OpenMapTiles sowohl in class als auch subclass.
      filter: [
        'any',
        ['==', ['get', 'class'], 'fast_food'],
        ['==', ['get', 'subclass'], 'fast_food'],
      ],
      layout: {
        'icon-image': overpassIconKey('fast_food'),
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });
  }
}

/** Schaltet die Sichtbarkeit der zwei Layer (idempotent, wirft nicht). */
export function setVectorPoiVisibility(
  map: maplibregl.Map,
  opts: { drinkingWater: boolean; fastFood: boolean },
): void {
  const set = (id: string, on: boolean) => {
    try {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
      }
    } catch {
      // Style noch nicht ready / Map zerstört → nächster Trigger holt's nach.
    }
  };
  set(VECTOR_POI_DW_LAYER, opts.drinkingWater);
  set(VECTOR_POI_FF_LAYER, opts.fastFood);
}

/**
 * Übersetzt ein angetipptes Vektor-poi-Feature in die POI-Popup-Shape, die
 * MapPage/CreateCustomPage verwenden (dieselben Felder wie der Blob-Click-
 * Handler). Vektor-Features haben KEIN lat/lon (→ Geometrie) und keine
 * Öffnungszeiten (→ leer).
 */
export function vectorPoiFeatureToPopup(
  feat: maplibregl.MapGeoJSONFeature,
): {
  id: string;
  category: 'drinking_water' | 'fast_food';
  name: string;
  lat: number;
  lon: number;
} | null {
  const props = feat.properties || {};
  const subclass = String(props.subclass ?? '');
  const klass = String(props.class ?? '');
  const category: 'drinking_water' | 'fast_food' | null =
    subclass === 'drinking_water'
      ? 'drinking_water'
      : klass === 'fast_food' || subclass === 'fast_food'
        ? 'fast_food'
        : null;
  if (!category) return null;
  const geom = feat.geometry as GeoJSON.Point | undefined;
  const coords = geom?.coordinates as [number, number] | undefined;
  return {
    // Vektor-Feature-id ist die OSM-id; Prefix hält sie disjunkt von Blob-ids.
    id: `vt:${feat.id ?? `${category}:${coords?.join(',') ?? '?'}`}`,
    category,
    name: String(props.name ?? ''),
    lat: coords?.[1] ?? 0,
    lon: coords?.[0] ?? 0,
  };
}

/** Tear-down: beide Layer entfernen. Icons + Source bleiben. Wirft nicht. */
export function removeVectorPoiLayers(map: maplibregl.Map): void {
  try {
    if (map.getLayer(VECTOR_POI_DW_LAYER)) map.removeLayer(VECTOR_POI_DW_LAYER);
    if (map.getLayer(VECTOR_POI_FF_LAYER)) map.removeLayer(VECTOR_POI_FF_LAYER);
  } catch {
    // Map bereits zerstört — nichts zu tun.
  }
}
